import React, { useMemo, useState } from 'react';
import { Container } from 'reactstrap';
import Layout from '../../templates/Layout';
import { useMutation, useQuery } from '@apollo/client';
import { GET_PERMITS_BY_ASSET_ID } from '../../../graph-queries/tar/permit';
import { TPermitWithJobDetails } from '../../../lib/types';
import { useIsFeatureFlagEnabled, useSelectedAsset } from '../../../lib/util';
import { useHasTarAdminAccess, useHasUserAccess } from '../../../lib/util/auth';
import AspectsTable from '../../organisms/AspectsTable/AspectsTable';
import TaskPermitForm from '../../organisms/TaskPermitForm/TaskPermitForm';
import { UPSERT_TASK_PERMIT } from '../../../graph-queries/tar/job';
import { useNoticeBar } from '../../molecules/NoticeBar/NoticeBar';
import { useNavigate } from 'react-router-dom';
import {
  downloadPermitNumbers,
  permitsGlobalFilterFn,
  usePermitsWithDetails,
} from './Permits.util';
import { EMPTY_PERMIT_FILTERS } from '../../../lib/constants';
import { useBrowserLocationState } from '../../../lib/util/browser';
import PermitFiltersModal from '../../organisms/PermitFiltersModal/PermitFiltersModal';
import PermitDetailsModal from './PermitDetailsModal';
import { Spinner, Alert, Card } from '@bphxd/ds-core-react';
import {
  Alert16,
  Alert24,
  Check16,
  DownloadExcel24,
} from '@bphxd/ds-core-react/lib/icons';
import { useAlertText } from '../../organisms/AspectsTable/util';
import { TARS_AND_OUTAGES_PATH } from '../../../constants';

const ACTION_DISABLED_WHILE_LOADING_MESSAGE = 'Data updation in progress';

const PermitsPage: React.FC = () => {
  const hasUserAccess = useHasUserAccess();
  const hasTarAdminAccess = useHasTarAdminAccess();
  const asset = useSelectedAsset();
  const navigate = useNavigate();
  const notice = useNoticeBar();
  const isFiltersEnabled = useIsFeatureFlagEnabled('FEATURE_FLAG_ENABLE_JOBS_FILTER');
  const [filters, setFilters] = useBrowserLocationState('filters', EMPTY_PERMIT_FILTERS);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { alertText, setAlertText } = useAlertText();
  const [permitCount, setPermitCount] = useState(0);
  const [uniquePermits, setUniquePermits] = useState<string[]>([]);

  const { data, loading, error } = useQuery(GET_PERMITS_BY_ASSET_ID, {
    variables: { assetId: asset?.assetId || -1 },
    skip: !asset?.assetId,
  });

  const permits = (data?.permitsByAssetId as unknown as TPermitWithJobDetails[]) || [];
  const { data: permitsWithDetails, loading: permitsWithDetailsLoading } =
    usePermitsWithDetails(permits);
  const filteredPermits = useMemo(
    () => permitsWithDetails.filter((permit) => permit.taskDescription),
    [permitsWithDetails]
  );

  const [upsertTaskPermit, { loading: isUpdating }] = useMutation(UPSERT_TASK_PERMIT, {
    refetchQueries: [GET_PERMITS_BY_ASSET_ID],
    awaitRefetchQueries: true,
  });

  const [selectedPermit, setSelectedPermit] = useState<TPermitWithJobDetails | null>(null);

  const handleModalClose = () => setSelectedPermit(null);
  const handleDownload = async (uniquePermits: string[]) => downloadPermitNumbers(uniquePermits);

  const isLoading = isUpdating || loading || !!alertText;
  return (
    <Layout>
      <Container>
        <Card hoverShadow className="h-100">
          <Card.Body className="pt-4 d-flex flex-column">
            <div className="d-flex">
              <div className="flex-grow-1">
                <Card.Title className="mb-4 d-flex align-items-center" tag="h5">
                  Permit Count
                  <DownloadExcel24
                    onClick={() => handleDownload(uniquePermits)}
                    title="Click to download to Excel"
                    style={{ cursor: 'pointer' }}
                  ></DownloadExcel24>
                </Card.Title>
              </div>
            </div>
            {permitsWithDetailsLoading ||
              (loading && (
                <div className="d-flex justify-content-center align-items-center flex-grow-1">
                  <Spinner />
                  <span className="visually-hidden">Loading...</span>
                </div>
              ))}
            {error && (
              <div className="d-flex justify-content-center align-items-center flex-grow-1">
                <Alert24 title="error" />
              </div>
            )}
            {!permitsWithDetailsLoading && !loading && !error && (
              <>
                <div className="d-flex mb-3 fs-6">
                  <Card.Text className="mb-0">{permitCount}</Card.Text>
                </div>
                <Card.Subtitle>Unique filtered permits</Card.Subtitle>
              </>
            )}
          </Card.Body>
        </Card>
        {(loading || permitsWithDetailsLoading) && (
          <div className="d-flex justify-content-center align-items-center vh-100">
            <Spinner size="lg" className="ms-3" label="Loading Permit Data" />
          </div>
        )}
        {isFilterOpen && (
          <PermitFiltersModal
            defaultValues={filters}
            onSubmit={setFilters}
            onClose={() => setIsFilterOpen(false)}
          />
        )}
        {!loading && !permitsWithDetailsLoading && (
          <div>
            <AspectsTable<TPermitWithJobDetails>
              hideSelectionColumn
              aspectName="permit"
              data={filteredPermits || []}
              columns={[
                {
                  header: 'Job',
                  accessorKey: 'jobId',
                  cell: (context) => {
                    const jobIds = Array.from(
                      new Set(context.table.getSortedRowModel().rows.map((r) => r.original.jobId))
                    );

                    const row = context.row.original;
                    return (
                      <a
                        href={`/${TARS_AND_OUTAGES_PATH}/jobs/${row.jobId}${location.search}`}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(
                            `/${TARS_AND_OUTAGES_PATH}/jobs/${row.jobId}${location.search}`,
                            { state: { jobIds } }
                          );
                        }}
                        style={{ color: 'blue', textDecoration: 'underline', cursor: 'pointer' }}
                      >
                        {row.jobId}
                      </a>
                    );
                  },
                },
                {
                  header: 'MO Number',
                  accessorKey: 'moNumber',
                  cell: ({ row }) => {
                    const moNumber = row.original.moNumber;
                    const href = (row.original as TPermitWithJobDetails)?.viewInFiori;
                    return href ? (
                      <a href={href} target="_blank" rel="noreferrer">
                        {moNumber}
                      </a>
                    ) : (
                      <>{moNumber}</>
                    );
                  },
                },
                {
                  header: 'FLOC',
                  accessorKey: 'functionalLocation',
                },
                {
                  header: 'Task',
                  accessorKey: 'taskId',
                },
                {
                  header: 'Task Description',
                  accessorKey: 'taskDescription',
                },
                {
                  header: 'Work Center',
                  accessorKey: 'taskWorkCenter',
                },
                {
                  header: 'Permit Requirement',
                  accessorKey: 'permitRequirement',
                },
                {
                  header: 'Permit Number',
                  accessorKey: 'permitNumber',
                  cell: (context) => {
                    const permitNumber = context.getValue() as string;
                    if (permitNumber) {
                      const formattedPermitNumber = permitNumber.replace('#', '%23');
                      const url = `https://ecow.bpglobal.com/permitvision/permitvision/editor/certificate/${formattedPermitNumber}?openerRegistrationNumber=Permit`;

                      return (
                        <a href={url} target="_blank" rel="noreferrer">
                          {permitNumber}
                        </a>
                      );
                    }
                    return null;
                  },
                },
                {
                  header: 'Permit Status',
                  accessorKey: 'ecowPermit.statusComment',
                },
                {
                  header: 'Permit Owner',
                  accessorKey: 'permitOwner',
                },
                {
                  header: 'Permit Walked',
                  accessorKey: 'permitWalked',
                },
                {
                  header: 'Comments',
                  accessorKey: 'comments',
                  enableSorting: false,
                },
                {
                  header: 'Integrity Check',
                  enableSorting: false,
                  cell: ({ row }) => {
                    return row.original.lastKnownTaskDescription ===
                      row.original.taskDescription ? (
                      <Check16 />
                    ) : (
                      <Alert16
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedPermit(row.original)}
                      />
                    );
                  },
                },
              ]}
              renderEditForm={(onClose, defaultValue) => (
                <TaskPermitForm
                  defaultValues={defaultValue}
                  taskDescription={defaultValue.taskDescription || ''}
                  onSubmit={async (taskPermit) => {
                    taskPermit.moNumber = defaultValue.moNumber;
                    return upsertTaskPermit({ variables: { input: taskPermit } }).then(() => {
                      setAlertText('Task details saved');
                    });
                  }}
                  onClose={onClose}
                />
              )}
              alertText={alertText}
              creationDisabled={!hasUserAccess}
              getRowMutationDisabled={(_row) => !hasUserAccess}
              getRowDeletionDisabled={(_row) => !hasTarAdminAccess}
              searchEnabled
              searchPlaceholderText="Search the permit tracker..."
              isLoading={isLoading}
              globalFilterFn={permitsGlobalFilterFn}
              filters={filters}
              onFilterClick={(isFiltersEnabled && (() => setIsFilterOpen(true))) || undefined}
              onUpdateFilters={(newFilters) => setFilters(newFilters)}
              onFilteredRowsChange={(filteredRows) => {
                const permits = filteredRows
                  .map((row) => row.getValue('permitNumber'))
                  .filter(
                    (permitNumber): permitNumber is string =>
                      typeof permitNumber === 'string' && permitNumber.trim() !== ''
                  );

                const uniquePermits = Array.from(new Set(permits)); // string[]

                setPermitCount(uniquePermits.length);
                setUniquePermits(uniquePermits);
              }}
            />
          </div>
        )}
        <PermitDetailsModal permit={selectedPermit} onClose={handleModalClose} />
      </Container>
    </Layout>
  );
};

export default PermitsPage;
